import { type BubbleSide, placeBubble } from 'BomdisoftComponents/tooltip/placement';
import { TOOLTIP_TRIGGER_ATTRIBUTE } from 'BomdisoftComponents/tooltip/render';

const SHOW_DELAY_MS = 300;
const HIDE_DELAY_MS = 120;
const SIDES: readonly BubbleSide[] = ['bottom', 'left', 'right', 'top'];
const TRIGGER = `[${TOOLTIP_TRIGGER_ATTRIBUTE}]`;

type Timers = { close?: ReturnType<typeof setTimeout>; open?: ReturnType<typeof setTimeout> };

const timers = new WeakMap<Element, Timers>();
const openByView = new WeakMap<Window, Set<HTMLElement>>();
const followers = new WeakMap<Window, () => void>();
const delegatedRoots = new WeakSet<Document | ShadowRoot>();

const asElement = (target: EventTarget | null): Element | null => (target !== null && typeof (target as Element).closest === 'function' ? (target as Element) : null);

const timersOf = (trigger: Element) => {
  const existing = timers.get(trigger);
  if (existing !== undefined) return existing;
  const created: Timers = {};
  timers.set(trigger, created);
  return created;
};

const openSetOf = (view: Window) => {
  const existing = openByView.get(view);
  if (existing !== undefined) return existing;
  const created = new Set<HTMLElement>();
  openByView.set(view, created);
  return created;
};

const bubbleOf = (trigger: Element): HTMLElement | null => {
  const next = trigger.nextElementSibling;
  return next !== null && next.getAttribute('role') === 'tooltip' ? (next as HTMLElement) : null;
};

const triggerOf = (bubble: Element): HTMLElement | null => {
  const previous = bubble.previousElementSibling;
  return previous?.hasAttribute(TOOLTIP_TRIGGER_ATTRIBUTE) ? (previous as HTMLElement) : null;
};

const preferredSide = (trigger: Element): BubbleSide => {
  const side = trigger.getAttribute(TOOLTIP_TRIGGER_ATTRIBUTE);
  return SIDES.find(candidate => candidate === side) ?? 'top';
};

const place = (trigger: HTMLElement, bubble: HTMLElement, view: Window) => {
  const position = placeBubble(trigger.getBoundingClientRect(), bubble.getBoundingClientRect(), { height: view.innerHeight, width: view.innerWidth }, preferredSide(trigger));
  bubble.setAttribute('data-placement', position.side);
  bubble.style.top = `${position.top}px`;
  bubble.style.left = `${position.left}px`;
};

const unfollow = (view: Window) => {
  const placeAll = followers.get(view);
  if (placeAll === undefined) return;
  followers.delete(view);
  view.removeEventListener('resize', placeAll);
  view.removeEventListener('scroll', placeAll, true);
  view.removeEventListener('transitionend', placeAll, true);
  view.removeEventListener('pointermove', placeAll);
};

const follow = (view: Window) => {
  if (followers.has(view)) return;
  const placeAll = () => {
    const open = openSetOf(view);
    for (const trigger of open) {
      const bubble = bubbleOf(trigger);
      if (trigger.isConnected && bubble !== null) place(trigger, bubble, view);
      else open.delete(trigger);
    }
    if (open.size === 0) unfollow(view);
  };
  followers.set(view, placeAll);
  view.addEventListener('resize', placeAll);
  view.addEventListener('scroll', placeAll, true);
  view.addEventListener('transitionend', placeAll, true);
  view.addEventListener('pointermove', placeAll);
};

const open = (trigger: HTMLElement) => {
  const bubble = bubbleOf(trigger);
  const view = trigger.ownerDocument.defaultView;
  if (bubble === null || view === null || !trigger.isConnected) return;
  const openTriggers = openSetOf(view);
  if (!openTriggers.has(trigger)) {
    bubble.showPopover();
    openTriggers.add(trigger);
  }
  trigger.setAttribute('aria-describedby', bubble.id);
  place(trigger, bubble, view);
  follow(view);
};

const close = (trigger: HTMLElement) => {
  trigger.removeAttribute('aria-describedby');
  const view = trigger.ownerDocument.defaultView;
  if (view === null) return;
  const openTriggers = openSetOf(view);
  if (!openTriggers.delete(trigger)) return;
  const bubble = bubbleOf(trigger);
  if (bubble?.isConnected) bubble.hidePopover();
  if (openTriggers.size === 0) unfollow(view);
};

const show = (trigger: HTMLElement) => {
  const pending = timersOf(trigger);
  clearTimeout(pending.close);
  clearTimeout(pending.open);
  pending.open = setTimeout(() => open(trigger), SHOW_DELAY_MS);
};

const hide = (trigger: HTMLElement) => {
  const pending = timersOf(trigger);
  clearTimeout(pending.open);
  clearTimeout(pending.close);
  pending.close = setTimeout(() => close(trigger), HIDE_DELAY_MS);
};

const hideNow = (trigger: HTMLElement) => {
  const pending = timersOf(trigger);
  clearTimeout(pending.open);
  clearTimeout(pending.close);
  close(trigger);
};

const hoveredTrigger = (element: Element) => (element.hasAttribute(TOOLTIP_TRIGGER_ATTRIBUTE) ? (element as HTMLElement) : element.getAttribute('role') === 'tooltip' ? triggerOf(element) : null);

const triggersAround = (element: Element) => {
  const found: HTMLElement[] = [];
  for (let trigger = element.closest(TRIGGER); trigger !== null; trigger = trigger.parentElement?.closest(TRIGGER) ?? null) found.push(trigger as HTMLElement);
  return found;
};

const onHover = (act: (trigger: HTMLElement) => void) => (event: Event) => {
  const element = asElement(event.target);
  const trigger = element === null ? null : hoveredTrigger(element);
  if (trigger !== null) act(trigger);
};

const onWithin = (act: (trigger: HTMLElement) => void) => (event: Event) => {
  const element = asElement(event.target);
  if (element !== null) for (const trigger of triggersAround(element)) act(trigger);
};

const handleEnter = onHover(show);
const handleLeave = onHover(hide);
const handleFocusIn = onWithin(show);
const handleFocusOut = onWithin(hide);
const handlePress = onWithin(hideNow);

const viewOfRoot = (root: Document | ShadowRoot) => (root.nodeType === root.DOCUMENT_NODE ? (root as Document).defaultView : (root as ShadowRoot).ownerDocument.defaultView);

export const delegateTooltips = (root: Document | ShadowRoot = document) => {
  if (delegatedRoots.has(root)) return;
  delegatedRoots.add(root);
  root.addEventListener('mouseenter', handleEnter, true);
  root.addEventListener('mouseleave', handleLeave, true);
  root.addEventListener('focusin', handleFocusIn);
  root.addEventListener('focusout', handleFocusOut);
  root.addEventListener('pointerdown', handlePress, true);
  root.addEventListener('click', handlePress, true);
  root.addEventListener('keydown', event => {
    const view = viewOfRoot(root);
    if ((event as KeyboardEvent).key !== 'Escape' || view === null) return;
    for (const trigger of [...openSetOf(view)]) close(trigger);
  });
};
